<template>
  <div>
    <v-dialog
      v-model="isVisible"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      :overlay-opacity="0.85"
      max-width="350"
      persistent
      scrollable
    >
      <v-card v-if="order">
        <v-toolbar
          :color="getOrderTypeColor(order)"
          dark
          flat
          height="80"
          max-height="80"
        >
          <v-avatar
            v-if="getOrderSourceTypeById(order.SourceTypeId)"
            class="mr-2"
            color="rgba(0, 0, 0, 0.20)"
          >
            <v-icon
              v-if="order.SourceTypeId < 100"
              color="white"
              size="18"
            >
              {{ getOrderSourceTypeById(order.SourceTypeId).logo }}
            </v-icon>

            <img
              v-else
              :src="getOrderSourceTypeById(order.SourceTypeId).logo"
            >
          </v-avatar>

          <v-toolbar-title class="pl-3 body-3">
            <span v-if="order.SourceTypeId === SourceTypeEnum.TABLE">
              {{ order.TableArea }} #{{ order.TableName }}
            </span>

            <span v-else>
              #{{ order.OrderCode }}
            </span>

            <div
              v-if="order.DailyCheck && appConfig.LOCATION_DATA.ShowDailyCheck"
              class="text-caption"
            >
              <strong>{{ $t('Order.Headers.DailyCheck') }}:</strong>&nbsp;{{ order.DailyCheck }}
            </div>

            <div
              v-if="order.StaffId && parseInt(order.SourceTypeId) === SourceTypeEnum.TABLE && order.Persons"
              class="text-caption"
            >
              <strong>{{ $t('Reservation.Table.Headers.Persons') }}:</strong>&nbsp;{{ order.Persons }}
            </div>
          </v-toolbar-title>

          <v-spacer />

          <v-btn
            icon
            @click="closeDialog"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-divider />

        <v-card-text class="fill-height pa-3">
          <v-container
            class="pa-0"
            fluid
          >
            <v-row dense>
              <!-- Complete -->
              <v-col
                v-if="canCompleteKdsOrder(order)"
                cols="12"
              >
                <v-btn
                  :color="$t('Kds.Action.Order.Complete.Color')"
                  :disabled="!canCompleteKdsOrder(order) || loadingCompleteKdsOrder(order)"
                  :loading="loadingCompleteKdsOrder(order)"
                  block
                  class="px-3"
                  dark
                  depressed
                  x-large
                  @click="onKdsOrderCompleteClick(order)"
                >
                  <v-icon>
                    {{ $t('Kds.Action.Order.Complete.Icon') }}
                  </v-icon>

                  <v-spacer />
                  {{ $t('Kds.Action.Order.Complete.Name') }}
                  <v-spacer />
                </v-btn>
              </v-col>

              <!-- Revert -->
              <v-col
                v-if="canRevertKdsOrder(order)"
                cols="12"
              >
                <v-btn
                  :color="$t('Kds.Action.Order.Revert.Color')"
                  :disabled="!canRevertKdsOrder(order) || loadingRevertKdsOrder(order)"
                  :loading="loadingRevertKdsOrder(order)"
                  block
                  class="px-3"
                  dark
                  depressed
                  x-large
                  @click="onKdsOrderRevertClick(order)"
                >
                  <v-icon>
                    {{ $t('Kds.Action.Order.Revert.Icon') }}
                  </v-icon>

                  <v-spacer />
                  {{ $t('Kds.Action.Order.Revert.Name') }}
                  <v-spacer />
                </v-btn>
              </v-col>

              <!-- Hold -->
              <v-col
                v-if="canHoldKdsOrder(order)"
                cols="12"
              >
                <v-btn
                  :color="$t('Kds.Action.Order.Hold.Color')"
                  :disabled="!canHoldKdsOrder(order) || loadingHoldKdsOrder(order)"
                  :loading="loadingHoldKdsOrder(order)"
                  block
                  class="px-2"
                  dark
                  depressed
                  x-large
                  @click="onKdsOrderHoldClick(order)"
                >
                  <v-icon>
                    {{ $t('Kds.Action.Order.Hold.Icon') }}
                  </v-icon>

                  <v-spacer />
                  {{ $t('Kds.Action.Order.Hold.Name') }}
                  <v-spacer />
                </v-btn>
              </v-col>

              <!-- Resume -->
              <v-col
                v-if="canResumeKdsOrder(order)"
                cols="12"
              >
                <v-btn
                  :color="$t('Kds.Action.Order.Resume.Color')"
                  :disabled="!canResumeKdsOrder(order) || loadingResumeKdsOrder(order)"
                  :loading="loadingResumeKdsOrder(order)"
                  block
                  class="px-2"
                  dark
                  depressed
                  x-large
                  @click="onKdsOrderResumeClick(order)"
                >
                  <v-icon>
                    {{ $t('Kds.Action.Order.Resume.Icon') }}
                  </v-icon>

                  <v-spacer />
                  {{ $t('Kds.Action.Order.Resume.Name') }}
                  <v-spacer />
                </v-btn>
              </v-col>

              <template v-if="canMoveForwardKdsOrder(order) || canMoveBackwardKdsOrder(order) || canMoveFirstKdsOrder(order) || canMoveLastKdsOrder(order)">
                <v-col cols="12">
                  <v-divider />
                </v-col>

                <!-- Move Forward / Backward -->
                <v-col cols="6">
                  <v-btn
                    :color="$t('Kds.Action.Order.Forward.Color')"
                    :disabled="!canMoveForwardKdsOrder(order)"
                    :loading="loadingMoveForwardKdsOrder(order)"
                    block
                    class="px-2"
                    dark
                    depressed
                    x-large
                    @click="onKdsOrderMoveForwardClick(order)"
                  >
                    <v-icon class="mr-3">
                      {{ $t('Kds.Action.Order.Forward.Icon') }}
                    </v-icon>

                    <v-spacer />
                    {{ $t('Kds.Action.Order.Forward.Name') }}
                    <v-spacer />
                  </v-btn>
                </v-col>
                <v-col cols="6">
                  <v-btn
                    :color="$t('Kds.Action.Order.Backward.Color')"
                    :disabled="!canMoveBackwardKdsOrder(order)"
                    :loading="loadingMoveBackwardKdsOrder(order)"
                    block
                    class="px-3"
                    dark
                    depressed
                    x-large
                    @click="onKdsOrderMoveBackwardClick(order)"
                  >
                    <v-spacer />
                    {{ $t('Kds.Action.Order.Backward.Name') }}
                    <v-spacer />

                    <v-icon class="ml-2">
                      {{ $t('Kds.Action.Order.Backward.Icon') }}
                    </v-icon>
                  </v-btn>
                </v-col>

                <!-- Move First / Last -->
                <v-col cols="6">
                  <v-btn
                    :color="$t('Kds.Action.Order.First.Color')"
                    :disabled="!canMoveFirstKdsOrder(order)"
                    :loading="loadingMoveFirstKdsOrder(order)"
                    block
                    class="px-3"
                    dark
                    depressed
                    x-large
                    @click="onKdsOrderMoveFirstClick(order)"
                  >
                    <v-icon class="mr-2">
                      {{ $t('Kds.Action.Order.First.Icon') }}
                    </v-icon>

                    <v-spacer />
                    {{ $t('Kds.Action.Order.First.Name') }}
                    <v-spacer />
                  </v-btn>
                </v-col>
                <v-col cols="6">
                  <v-btn
                    :color="$t('Kds.Action.Order.Last.Color')"
                    :disabled="!canMoveLastKdsOrder(order)"
                    :loading="loadingMoveLastKdsOrder(order)"
                    block
                    class="px-3"
                    dark
                    depressed
                    x-large
                    @click="onKdsOrderMoveLastClick(order)"
                  >
                    <v-spacer />
                    {{ $t('Kds.Action.Order.Last.Name') }}
                    <v-spacer />

                    <v-icon class="ml-2">
                      {{ $t('Kds.Action.Order.Last.Icon') }}
                    </v-icon>
                  </v-btn>
                </v-col>
              </template>
            </v-row>
          </v-container>
        </v-card-text>

        <v-divider />

        <v-card-actions class="px-3">
          <v-spacer />

          <v-btn
            class="light-blue--text text--darken-1"
            outlined
            text
            @click="closeDialog"
          >
            {{ $t('Common.Button.Close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import KdsOrderCommon from '@/mixins/kds/kdsOrderCommon.js'
import KdsOrderActions from '@/mixins/kds/kdsOrderActions.js'
import DialogVisible from '@/mixins/dialogVisible.js'
import OrderType from '@/mixins/orders/orderType.js'
import OrderSourceType from '@/mixins/orders/sourceType.js'
import SourceTypeEnum from '@/api/Enums/SourceTypeEnum.js'

export default {
  name: 'KdsOrderActionsDialog',

  components: {},

  mixins: [DialogVisible, OrderType, OrderSourceType, KdsOrderCommon, KdsOrderActions],

  props: {
    order: {
      type   : Object,
      default: undefined
    }
  },

  data() {
    return {}
  },

  computed: {
    SourceTypeEnum() {
      return SourceTypeEnum
    },

    selectedOrder() {
      return this.order
    }
  },

  watch: {},

  methods: {}
}
</script>

<style scoped>

</style>
